import * as React from 'react';
import {connect} from 'react-redux';

import {api} from 'nutshell-core/api';
import type {ListQuery} from 'nutshell-core/entities/fetch-list';
import * as Leads from 'nutshell-core/leads';
import type {ListItem, LeadPatchStageApiResponse} from 'nutshell-core/types';
import {safelyParseError} from 'nutshell-core/utils';
import {VisualPipeline} from 'nutshell-core/visual-pipeline';

import {Button} from 'shells/button/button';
import {Card} from 'shells/card/card';
// @ts-expect-error
import {LoadingIcon} from 'shells/icon/svg/loading-icon';

import type {NutshellState} from '../../../store';

import {LeadCardDynamicWrapper} from '../../lead/lead-card-dynamic';

import styles from './visual-pipeline-confirmation-column.module.css';

// @ts-expect-error
const $ = window.jQuery;

type OwnProps = {
    leadId: string;
    stageId: string;
    selectedSort: string;
    fieldsToDisplay: string[];
};

type StateProps = {
    listItemsById: {
        [key: string]: ListItem;
    };
};

type DispatchProps = {
    onCancelStageTransition: () => any;
    onCompleteStageTransition: (leadId: string, query: ListQuery) => any;
};

const mapStateToProps = function (state: NutshellState): StateProps {
    return {
        // @ts-expect-error
        listItemsById: Leads.getLeadListItemMap(state),
    };
};

// @ts-expect-error
const mapDispatchToProps = function (dispatch): DispatchProps {
    return {
        onCancelStageTransition: () => {
            dispatch(
                VisualPipeline.updateLeadStageTransition({
                    leadId: null,
                    oldStageId: null,
                    newStageId: null,
                })
            );
        },
        onCompleteStageTransition: (leadId, query) => {
            dispatch(VisualPipeline.onLeadStageTransitionComplete(leadId, query));
        },
    };
};

type Props = OwnProps & StateProps & DispatchProps;

/*
    Component in charge of displaying and maintaining state around a lead
    transitioning to a new stage. This involves making requests to see
    if the lead _can even move_ to this stage, and then making a subsequent
    request to actually change the stage of the lead.

    Also handles all loading, error, and error retrying states.
 */

export class VisualPipelineConfirmationColumnComponent extends React.Component<
    Props,
    {
        isLoading: boolean;
        confirmationMessage: string | null | undefined;
        errorMessage?: string;
        canRetryError: boolean;
    }
> {
    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isLoading: true,
            canRetryError: true,
            confirmationMessage: null,
        };
    }

    UNSAFE_componentWillMount() {
        this.getLeadTransitions();
    }

    render() {
        const listItem = this.props.listItemsById[this.props.leadId];

        if (this.state.errorMessage) {
            return (
                <div className={styles.container}>
                    <div className={styles['card-container']}>
                        <LeadCardDynamicWrapper
                            listItem={listItem}
                            hasActionMenu={false}
                            linkable={false}
                            fieldsToDisplay={this.props.fieldsToDisplay}
                        />
                        <Card hasBorder={true} borderColor={'red'} hasShadow={true}>
                            <div className={styles['error-message-container']}>
                                <div className={styles['error-message']}>
                                    {this.state.errorMessage}
                                </div>
                                <div className={styles['error-buttons']}>
                                    {this.state.canRetryError ? (
                                        <Button
                                            onClick={this.handleLeadTransitionsErrorRetry}
                                            variant='secondary'
                                            size='big'
                                        >
                                            Retry
                                        </Button>
                                    ) : undefined}
                                    <Button
                                        onClick={this.handleStageChangeCancel}
                                        variant='primary'
                                        size='big'
                                    >
                                        Okay
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.container}>
                <div className={styles['card-container']}>
                    {this.state.isLoading ? (
                        <LoadingIcon className={styles['loading-icon']} />
                    ) : (
                        <LeadCardDynamicWrapper
                            listItem={listItem}
                            hasActionMenu={false}
                            linkable={false}
                            fieldsToDisplay={this.props.fieldsToDisplay}
                        />
                    )}
                    {this.state.confirmationMessage ? (
                        <Card hasBorder={false}>
                            <div className={styles['confirmation-message-container']}>
                                <div className={styles['confirmation-message']}>
                                    {this.state.confirmationMessage}
                                </div>
                                <div className={styles['confirmation-buttons']}>
                                    <Button
                                        onClick={this.handleStageChangeCancel}
                                        variant='secondary'
                                        size='big'
                                    >
                                        No
                                    </Button>
                                    <Button
                                        onClick={this.handleStageChangeConfirm}
                                        variant='primary'
                                        size='big'
                                    >
                                        Yes
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    ) : undefined}
                </div>
            </div>
        );
    }

    /**
     * Request that is made when a user has dropped the a lead card
     * in a new stage column. We don't immediately know if they can make that
     * stage change (sometimes there are required steps they can't skip, and
     * sometimes there are steps that _are_ skippable, but need a confirmation
     * from the user).
     *
     * If this request returns "no confirmation needed", we'll just immediately
     * fire our stage change request.
     *
     * @return {void}
     */
    getLeadTransitions = () => {
        // Reset our loading state before making request
        this.setState({
            isLoading: true,
            errorMessage: undefined,
        });

        requestCanLeadChangeStage(this.props.leadId, this.props.stageId).then(
            (res) => {
                if (res.meta.confirmMessage) {
                    this.setState({
                        isLoading: false,
                        confirmationMessage: res.meta.confirmMessage,
                    });
                } else {
                    // If no confirmation message, that means we can
                    // successfully make this stage change without needing
                    // to skip any steps, so we'll just auto-confirm here
                    this.handleStageChangeConfirm();
                }
            },
            (err) => {
                const errorMessage = err.name ? err.name : safelyParseError(err);

                const errorStatus = err.httpResponse ? err.httpResponse.status : err.status;

                this.setState({
                    isLoading: false,
                    errorMessage: errorMessage || 'Could not move stage right now',
                    // If 409 (Conflict), retrying won't fix the error, so prevent
                    // user from doing so
                    canRetryError: errorStatus !== 409,
                });
            }
        );
    };

    /**
     * Makes the actual request to change a lead's stage. This is called
     *
     * a) When the user has manually confirmed a blocking stage change message
     * (i.e. "Moving to this stage will skip three steps, is that OK?") or
     *
     * b) When the user has dragged a stage to a different column, and we've
     * determined there are _not_ any blockers to changing this stage
     *
     * @return {void}
     */
    handleStageChangeConfirm = () => {
        this.setState({isLoading: true});

        changeLeadStage(this.props.leadId, this.props.stageId).then(
            () => {
                // Fire callback which dispatches action letting our state
                // know that we've successfully changed a stage
                this.props.onCompleteStageTransition(this.props.leadId, {
                    sort: this.props.selectedSort,
                });
            },
            () => {
                this.setState({
                    errorMessage: 'Could not change stage',
                });
            }
        );
    };

    // Dispatch an action to redux to inform our state that a lead is
    // no longer transitioning stages (we've cancelled that operation)
    handleStageChangeCancel = () => {
        this.props.onCancelStageTransition();
    };

    handleLeadTransitionsErrorRetry = () => {
        this.getLeadTransitions();
    };
}

/**
 * Helper function to just take a lead and new stageId, and see what kind
 * of confirmation message to show the user ("Moving to this stage will
 * skip three steps…")
 *
 * Once again, this request _is not_ changing stage, it's merely for _seeing_
 * if we can make that request with / without confirmation from the user.
 * @param  {string} leadId     -        api id of lead wanting to change stage
 * @param  {string} newStageId -        stageId to check if the lead can move to
 * @return {Promise}           -        jQuery ajax return
 */
function requestCanLeadChangeStage(leadId: string, newStageId: string) {
    return (
        api
            .get(`leads/${leadId}`, {patchStage: newStageId})
            // @ts-expect-error
            .then((response: Response): Promise<LeadPatchStageApiResponse> => response.json())
    );
}

/**
 * Helper function to make a PATCH request to actually change a lead's stage.
 * We'll never make this request unless we know it's allowed (i.e. we won't
 * fire this request on a lead that has required, unskippable steps).
 *
 *
 * @param  {string} leadId      -         api id of lead changing stage
 * @param  {string} newStageId  -         stageId to move lead to
 * @return {Promise}            -         jQuery ajax return
 */
function changeLeadStage(leadId: string, newStageId: string) {
    const data = [
        {
            op: 'replace',
            path: '/leads/0/links/stage',
            value: newStageId,
        },
    ];

    return $.ajax({
        url: `/rest/leads/${leadId}`,
        type: 'PATCH',
        data: JSON.stringify(data),
        headers: {
            'Content-Type': 'application/json-patch+json',
        },
    });
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const VisualPipelineConfirmationColumn = connector(
    VisualPipelineConfirmationColumnComponent
);
